import React from 'react';
import { Question, QuestionOption } from '../data/questions';
import { useLanguage } from '../context/LanguageContext';

interface QuestionCardProps {
  question: Question;
  onAnswer: (questionId: string, answer: string) => void;
  currentAnswer?: string;
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question, onAnswer, currentAnswer }) => {
  const { t, language } = useLanguage();
  
  // Special layout for hand size question
  const isHandSizeQuestion = question.id === 'handSize';
  // Special layout for usage type question (more options)
  const isUsageTypeQuestion = question.id === 'usageType';
  
  // Get specific background image based on question type
  const getBackgroundImage = () => {
    if (question.id === 'handedness') {
      return '/left_right.jpg';
    } else if (question.id === 'usageType') {
      return '/vineyard.jpg';
    } else if (question.id === 'userType') {
      return '/home-garden.jpg';
    } else {
      return `/images/${question.id}-background.jpg`;
    }
  };

  // Get translated question text
  const getQuestionText = () => {
    return t(`question.${question.id}`);
  };

  // Get translated help text
  const getHelpText = () => {
    return t(`question.${question.id}.help`);
  };

  // Get translated option label
  const getOptionLabel = (option: QuestionOption) => {
    return t(`option.${option.value}`);
  };

  // Get hand size image based on current language
  const getHandSizeImage = () => {
    const imageMap: Record<string, string> = {
      'en': '/Product selector - hand sizer_mobile dimensions CM_INCHES.jpg',
      'de': '/Product selector - hand sizer_mobile dimensions DE.jpg',
      'fr': '/Product selector - hand sizer_mobile dimensions FR.jpg',
      'nl': '/Product selector - hand sizer_mobile dimensions NL.jpg',
      'es': '/Product selector - hand sizer_mobile dimensions CM_INCHES.jpg', // Default to English for Spanish
      'it': '/Product selector - hand sizer_mobile dimensions IT.jpg',
    };
    
    return imageMap[language] || imageMap['en'];
  };
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all border border-gray-100">
      <div className="flex flex-col md:flex-row">
        {/* Large image section for all question types except hand size */}
        {!isHandSizeQuestion && (
          <div className="w-full md:w-1/2">
            <div className="h-64 md:h-full w-full relative overflow-hidden">
              <img 
                src={getBackgroundImage()}
                alt={getQuestionText()}
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "https://via.placeholder.com/800x600/f8f9fa/cccccc?text=FELCO";
                }}
              />
            </div>
          </div>
        )}
        
        {/* Question and options section */}
        <div className={`p-8 ${isHandSizeQuestion ? 'w-full' : 'w-full md:w-1/2'}`}>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">{getQuestionText()}</h2>
          
          {question.helpText && (
            <p className="text-gray-600 mb-8 text-lg">{getHelpText()}</p>
          )}
          
          {isHandSizeQuestion ? (
            <div className="flex flex-col md:flex-row items-center justify-between">
              {/* Answers Section - Left Side */}
              <div className="w-full md:w-1/2 md:pr-8">
                <div className="flex flex-col space-y-4">
                  {question.options.map((option: QuestionOption) => (
                    <button
                      key={option.value}
                      className={`p-5 border-2 rounded-lg transition-all ${
                        currentAnswer === option.value 
                          ? 'border-felco-red bg-felco-red bg-opacity-5 text-felco-red font-medium' 
                          : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => onAnswer(question.id, option.value)}
                    >
                      <span className="text-xl">{getOptionLabel(option)}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Hand Sizer Image - Right Side */}
              <div className="w-full md:w-1/2 mt-8 md:mt-0">
                <img 
                  src={getHandSizeImage()}
                  alt="Hand Size Measurement Guide"
                  className="w-[110%] max-w-[110%] mx-auto rounded-lg shadow-md"
                  style={{ clipPath: 'inset(0 0 10% 0)' }}
                />
              </div>
            </div>
          ) : isUsageTypeQuestion ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-2">
              {question.options.map((option: QuestionOption) => (
                <button
                  key={option.value}
                  className={`flex items-center p-3 border-2 rounded-lg transition-all ${
                    currentAnswer === option.value 
                      ? 'border-felco-red bg-felco-red bg-opacity-5 text-felco-red' 
                      : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => onAnswer(question.id, option.value)}
                >
                  <span className="text-base">{getOptionLabel(option)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {question.options.map((option: QuestionOption) => (
                <button
                  key={option.value}
                  className={`flex items-center p-4 border-2 rounded-lg transition-all ${
                    currentAnswer === option.value 
                      ? 'border-felco-red bg-felco-red bg-opacity-5 text-felco-red' 
                      : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => onAnswer(question.id, option.value)}
                >
                  <span className="text-xl">{getOptionLabel(option)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionCard; 